import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Spin,
  Row,
  Col,
  Card,
  message,
  Tag,
  Alert,
  Space,
  InputNumber,
  Tooltip,
} from 'antd'
import {
  CrownOutlined,
  ThunderboltOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  DollarCircleOutlined,
  TeamOutlined,
  StarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import backendApis from '../../utils/backendApis'
import commaNumber from 'comma-number'
import bithumbLogo from '../../images/summerBlackFriday/bithumb_logo_RGB_orange_가로형.png'

const PremiumBrandApplicationModal = ({
  showModal,
  setShowModal,
  onClose,
  sellerInfo,
  onSuccess,
  premiumRegistrationStatus,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedItemIds, setSelectedItemIds] = useState([])
  const [eligibleItems, setEligibleItems] = useState([])
  const [expectedDiscountRate, setExpectedDiscountRate] = useState(20)
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false)

  // 신청 가능한 상품 목록 가져오기 - 모든 상품 로드
  const loadEligibleItems = async () => {
    setIsLoading(true)
    try {
      // 프리미엄 브랜드관 신청 가능 상품 조건
      const itemCondition = {
        soldOut: false,
        outOfStock: { $ne: null },
        // 높은 등급 상품 우선 (S, A 등급)
        'rankInfo.itemRank': { $in: ['S', 'A', 'B'] },
      }

      // 전체 상품 개수 가져오기
      const countResult = await backendApis.getSellerItemsCountByCondition({
        type: 'item',
        itemCondition,
      })

      const totalCount = countResult?.data || 0

      // 모든 상품을 배치로 가져오기 (성능 최적화)
      const batchSize = 500
      const totalPages = Math.ceil(totalCount / batchSize)
      let allItems = []

      for (let page = 1; page <= totalPages; page++) {
        const result =
          await backendApis.getSellerSegementedItemsInfoByCondition(
            {
              type: 'item',
              itemCondition,
            },
            page,
            batchSize,
          )

        if (result?.status === 2000 && result?.data?.itemsInfo) {
          allItems = [...allItems, ...result.data.itemsInfo]
        }
      }

      setEligibleItems(allItems)

      // 상위 20개 상품 자동 선택 (높은 등급 순으로 정렬 후)
      const sortedItems = allItems.sort((a, b) => {
        // S > A > B 순으로 정렬
        const rankOrder = { S: 3, A: 2, B: 1 }
        const rankA = rankOrder[a.rankInfo?.itemRank] || 0
        const rankB = rankOrder[b.rankInfo?.itemRank] || 0

        if (rankA !== rankB) return rankB - rankA

        // 같은 등급이면 판매량 순
        return (b.totalOrderQuantity || 0) - (a.totalOrderQuantity || 0)
      })

      const top20Items = sortedItems.slice(0, 20).map((item) => item._id)
      setSelectedItemIds(top20Items)
    } catch (error) {
      console.error('Load items error:', error)
      message.error('상품 정보를 불러오는데 실패했습니다')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!showModal) return

    // 모달이 열릴 때 프리미엄 브랜드 신청 상태 확인
    const checkRegistrationStatus = async () => {
      try {
        const result =
          await backendApis.getSummerBlackFridayPremiumBrandStatus()

        if (result?.status === 200 && result?.data) {
          const status = result.data.registrationStatus

          // 이미 신청했거나 승인된 경우 모달 닫기 (메시지 표시 안함)
          if (status === 'pending' || status === 'approved') {
            setShowModal(false)
            onClose && onClose()
            return false // 상품 로드하지 않음
          }
        }
        return true // 상품 로드 진행
      } catch (error) {
        // 에러 발생 시 조용히 처리하고 상품은 로드
        console.error('Status check error:', error)
        return true
      }
    }

    const init = async () => {
      const shouldLoadItems = await checkRegistrationStatus()
      if (shouldLoadItems) {
        loadEligibleItems()
      }
    }

    init()
  }, [showModal, onClose, setShowModal]) // 의존성 배열 수정

  // 상품 선택 처리
  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      // 50개 제한 제거
      setSelectedItemIds([...selectedItemIds, itemId])
    } else {
      setSelectedItemIds(selectedItemIds.filter((id) => id !== itemId))
    }
  }

  // 전체 선택/해제 핸들러 수정
  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = eligibleItems.map((item) => item._id) // 50개 제한 제거
      setSelectedItemIds(allIds)
    } else {
      setSelectedItemIds([])
    }
  }

  const handleSubmit = async () => {
    if (selectedItemIds.length === 0) {
      message.warning('신청할 상품을 선택해주세요.')
      return
    }

    if (!expectedDiscountRate || expectedDiscountRate < 5) {
      message.warning('예상 할인율을 5% 이상 입력해주세요.')
      return
    }

    setIsSubmitting(true)
    try {
      const registrationData = {
        sellerInfo: {
          sellerId: sellerInfo._id || sellerInfo.id,
          sellerDisplayName: sellerInfo.sellerDisplayName,
          businessNumber: sellerInfo.businessRegistrationNumber,
          representorName: sellerInfo.representorName,
          contactNumber: sellerInfo.contactNumber || sellerInfo.phoneNumber,
          email: sellerInfo.email,
        },
        selectedItemIds: selectedItemIds,
        selectedItemCount: selectedItemIds.length,
        expectedDiscountRate: expectedDiscountRate,
      }

      const result =
        await backendApis.submitSummerBlackFridayPremiumBrandRegistration(
          registrationData,
        )

      // result가 없거나 status가 없는 경우 처리
      if (!result) {
        message.error('서버 응답을 받지 못했습니다.')
        return
      }

      if (result.status === 200) {
        // 성공 메시지는 여기서만 표시
        message.success('프리미엄 브랜드관 신청이 완료되었습니다.')
        setShowModal(false)
        onSuccess && onSuccess()
      } else {
        // 서버에서 반환한 에러 메시지 표시
        message.error(result.message || '신청 처리 중 문제가 발생했습니다.')
      }
    } catch (error) {
      // catch 블록은 네트워크 에러 등 예외적인 경우에만
      console.error('Premium brand application error:', error)
      message.error('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const benefits = [
    {
      icon: <TrophyOutlined className='text-xl text-yellow-500' />,
      title: '프리미엄 노출',
      desc: '프로모션 페이지 고정 노출',
    },
    {
      icon: <TeamOutlined className='text-xl text-blue-500' />,
      title: '한정 50개',
      desc: '엄선된 브랜드만',
    },
    {
      icon: <DollarCircleOutlined className='text-xl text-green-500' />,
      title: '판매 수수료율',
      desc: '기본 수수료율(5.5%)과 동일',
    },
    {
      icon: <StarOutlined className='text-xl text-purple-500' />,
      title: '빗썸 특별 혜택',
      desc: '빗썸 캐시백 보장 지급',
    },
  ]

  const columns = [
    {
      title: (
        <Checkbox
          checked={
            selectedItemIds.length === eligibleItems.length &&
            eligibleItems.length > 0
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'select',
      width: 40,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={selectedItemIds.includes(record._id)}
          onChange={(e) => handleItemSelect(record._id, e.target.checked)}
        />
      ),
    },
    {
      title: '상품명',
      dataIndex: 'itemTitle',
      width: 280,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span className='text-xs'>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: '등급',
      dataIndex: 'rankInfo',
      width: 50,
      render: (rankInfo) => {
        const rank = rankInfo?.itemRank || 'N/A'
        const colorMap = { S: 'purple', A: 'blue', B: 'green', C: 'orange' }
        return (
          <Tag color={colorMap[rank] || 'default'} className='text-xs m-0'>
            {rank}
          </Tag>
        )
      },
    },
    {
      title: '판매가',
      dataIndex: 'individualPurchasePrice',
      width: 90,
      render: (price) => (
        <span className='text-xs'>{commaNumber(price)}원</span>
      ),
    },
    {
      title: '누적주문',
      dataIndex: 'totalOrderQuantity',
      width: 70,
      render: (qty) => <span className='text-xs'>{commaNumber(qty)}</span>,
    },
  ]

  return (
    <Modal
      visible={showModal}
      onCancel={() => {
        if (!isSubmitting) {
          setShowModal(false)
          onClose && onClose()
        }
      }}
      width={1100}
      footer={null}
      bodyStyle={{ padding: 0 }}
      title={null}
      centered
      className='premium-brand-modal'
    >
      {/* 컴팩트한 헤더 */}
      <div className='bg-gradient-to-r from-gray-900 to-gray-800 text-white px-5 py-3'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <img src={bithumbLogo} alt='Bithumb' className='h-7' />
            <div className='flex items-center gap-2'>
              <CrownOutlined className='text-yellow-400 text-xl' />
              <h2 className='text-xl font-bold m-0'>프리미엄 브랜드관</h2>
            </div>
          </div>
          <Tag color='gold' className='text-sm px-3 py-0.5'>
            한정 50개 브랜드
          </Tag>
        </div>
      </div>

      <div className='p-4'>
        {/* 혜택 카드 - 컴팩트 버전 */}
        <Row gutter={12} className='mb-3'>
          {benefits.map((benefit, index) => (
            <Col span={6} key={index}>
              <Card
                className='text-center h-full hover:shadow-md transition-shadow'
                size='small'
                bodyStyle={{ padding: '12px' }}
              >
                <div>{benefit.icon}</div>
                <h4 className='font-bold text-xs mt-1 mb-0'>{benefit.title}</h4>
                <p className='text-xs text-gray-600 m-0'>{benefit.desc}</p>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 안내사항 */}
        <Alert
          message='신청 안내'
          description={
            <div className='flex justify-between items-start'>
              <ul className='text-xs space-y-0.5 mb-0 pl-4'>
                <li>
                  심사를 통해 50개 브랜드를 선정하며, 결과는 개별 연락드립니다
                </li>
                <li>선정 시 광고비 500만원(VAT 포함)이 청구됩니다</li>
              </ul>
            </div>
          }
          type='info'
          showIcon
          icon={<InfoCircleOutlined />}
          className='mb-3'
        />

        {/* 상품 선택 영역 */}
        <div className='mb-3'>
          <div className='flex justify-between items-center mb-2'>
            <span className='text-sm font-semibold'>
              프로모션 참여 상품 선택
            </span>
            <Space size='small'>
              <span className='text-xs text-gray-500'>
                {commaNumber(selectedItemIds.length)}/
                {commaNumber(eligibleItems.length)}개 선택
              </span>
              <Button
                size='small'
                onClick={() =>
                  setSelectedItemIds(eligibleItems.map((item) => item._id))
                }
              >
                전체 선택
              </Button>
              <Button size='small' onClick={() => setSelectedItemIds([])}>
                선택 해제
              </Button>
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={eligibleItems}
            rowKey='_id'
            pagination={false}
            scroll={{ y: 220 }} // 높이 조정
            size='small'
            loading={isLoading}
            locale={{
              emptyText: '신청 가능한 상품이 없습니다',
            }}
            className='compact-table'
          />
        </div>

        {/* 신청 정보 요약 */}
        <div className='bg-gradient-to-r from-[#FF6C00]/10 to-orange-50 rounded-lg p-3 mb-3'>
          <Row gutter={24} align='middle'>
            <Col span={8}>
              <div className='text-center'>
                <div className='text-2xl font-bold text-[#FF6C00]'>
                  {commaNumber(selectedItemIds.length)}개
                </div>
                <div className='text-xs text-gray-600'>선택 상품</div>
              </div>
            </Col>
            <Col span={8}>
              <div className='text-center'>
                <div className='text-xs text-gray-600 mb-1'>
                  예상 프로모션 할인율 설정
                </div>
                <InputNumber
                  value={expectedDiscountRate}
                  onChange={(value) => setExpectedDiscountRate(value)}
                  min={5}
                  max={50}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace('%', '')}
                  style={{ width: '100px' }}
                />
              </div>
            </Col>
            <Col span={8}>
              <div className='text-center'>
                <div className='text-2xl font-bold text-[#FF6C00]'>
                  5,000,000원
                </div>
                <div className='text-xs text-gray-600'>광고비 (VAT 포함)</div>
              </div>
            </Col>
          </Row>
        </div>

        {/* 액션 버튼 */}
        <div className='flex justify-end gap-2'>
          <Button onClick={() => setShowModal(false)} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            type='primary'
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={selectedItemIds.length === 0}
            className='bg-[#FF6C00] hover:bg-[#FF6C00]/90'
            icon={<CrownOutlined />}
          >
            프리미엄 브랜드관 신청하기
          </Button>
        </div>
      </div>

      <style jsx>{`
        .compact-table :global(.ant-table-thead > tr > th) {
          padding: 4px 8px !important;
          font-size: 12px !important;
          background: #f5f5f5 !important;
        }
        .compact-table :global(.ant-table-tbody > tr > td) {
          padding: 4px 8px !important;
        }
        .compact-table :global(.ant-checkbox-wrapper) {
          margin: 0 !important;
        }
        .premium-brand-modal :global(.ant-modal-body) {
          max-height: 90vh;
          overflow-y: auto;
        }
      `}</style>
    </Modal>
  )
}

export default PremiumBrandApplicationModal
